import z from 'zod';
import type { ParsedPart, TemplateEnrichment } from 'components/AgentChat/chat/types/message.types';

/*
 * A chat message as the backend sends it, and the one conversion from that into what the chat renders.
 *
 * Its own module rather than part of the API in index.ts, because the socket reads messages too and
 * has no business pulling the RTK Query API in to do it — and because there must be only one of
 * these. There used to be two, one per transport, and the socket's had quietly stopped carrying the
 * intake summary id and the user message id that the history's still did.
 */

export const AuthorSchema = z.enum(['user', 'assistant']);

export const FileInfoSchema = z.object({
  url: z.string(),
  filename: z.string(),
});

export const ToolErrorSchema = z.object({
  type: z.string(),
  error: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  statusCode: z.number().int().nullable().optional(),
  details: z.unknown().nullable().optional(),
});

/**
 * One message, over REST or down the socket.
 *
 * The two transports agree on everything but the name of the text: the history calls it `text`, a
 * streamed `message_state` calls it `content`. Both are accepted so that one schema and one
 * conversion serve both. The parts, enrichment and intake summary are the backend's own types and
 * are only checked for shape as far as the chat relies on it.
 */
export const ChatMessageSchema = z.object({
  id: z.string(),
  author: AuthorSchema,
  /** Seconds since the epoch, on both transports. */
  timestamp: z.number(),
  text: z.string().nullable().optional(),
  content: z.string().nullable().optional(),
  invocationId: z.string().nullable().optional(),
  userMessageId: z.string().nullable().optional(),
  files: z.array(FileInfoSchema).nullable().optional(),
  toolErrors: z.array(ToolErrorSchema).nullable().optional(),
  parsedParts: z.custom<ParsedPart[]>(Array.isArray).optional(),
  displayParts: z.custom<ParsedPart[]>(Array.isArray).optional(),
  enrichment: z.custom<TemplateEnrichment>().optional(),
});

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export type SessionMessage = {
  id: string;
  invocationId?: string;
  userMessageId?: string;
  role: z.infer<typeof AuthorSchema>;
  content: string;
  files?: Array<{ filename: string; version: number; url: string }>;
  toolErrors?: Array<z.infer<typeof ToolErrorSchema>>;
  parsedParts?: ParsedPart[];
  displayParts?: ParsedPart[];
  enrichment?: TemplateEnrichment;
  timestamp: string;
  isLoading?: boolean;
};

/**
 * The message as the chat renders it, whichever transport it came down.
 *
 * Field by field rather than a spread, so the wire's names (`author`, `text`) and its nulls stay out
 * of what the chat reads — but that is also why a field left out here is silently missing on screen.
 * Anything the chat reads off a message has to be listed.
 */
export function toSessionMessage(message: ChatMessage): SessionMessage {
  return {
    id: message.id,
    invocationId: message.invocationId ?? undefined,
    userMessageId: message.userMessageId ?? undefined,
    role: message.author,
    content: message.content ?? message.text ?? '',
    files: message.files?.length
      ? message.files.map((file, i) => ({
          filename: file.filename,
          version: i,
          url: file.url,
        }))
      : undefined,
    toolErrors: message.toolErrors ?? undefined,
    parsedParts: message.parsedParts,
    displayParts: message.displayParts,
    enrichment: message.enrichment,
    timestamp: new Date(message.timestamp * 1000).toISOString(),
  };
}
